import {
  PayOverTimeAccountCardState,
  PayOverTimeItem,
  PayOverTimeItemsTile,
  RouteNavigationAction,
  TileStatus,
} from '@varo/graphql-schema-types';
import { HomeCardDependencies } from 'features/home/HomeScreen/dependencies/types';
import { AccountIconType } from 'shared/components/vxl/AccountIcon/AccountIcon.helpers';
import { HorizontalAccountCardProps } from 'shared/components/vxl/cards/HorizontalAccountCard/HorizontalAccountCard';
import { TagProps } from 'shared/components/vxl/Tag/Tag';
import { toTagType } from 'shared/components/vxl/Tag/Tag.helpers';
import {
  VXLComponent,
  VXLComponentId,
  VXLGroup,
  VXLGroupId,
} from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { Maybe } from 'shared/utils/types';

interface PayOverTimeCardNode extends VXLComponent {
  id: string;
  name: VXLComponentId.HORIZONTAL_ACCOUNT_CARD;
  props: HorizontalAccountCardProps;
}

interface PayOverTimeTileNode extends VXLGroup {
  id: string;
  name: VXLGroupId.COMPONENT_GROUP;
  props: {
    components: PayOverTimeCardNode[];
    gap?: number;
  };
}

const mapAccountStateToAnalytics = (
  state: PayOverTimeItem['state'],
): string | undefined => {
  switch (state) {
    case PayOverTimeAccountCardState.ActiveAutopayOff:
      return 'active_autopay_off';
    case PayOverTimeAccountCardState.ActiveAutopayOn:
      return 'active_autopay_on';
    case PayOverTimeAccountCardState.ChargedOff:
      return 'charged_off';
    case PayOverTimeAccountCardState.PaymentOverdue:
      return 'payment_overdue';
    default:
      return undefined;
  }
};

const mapTitleAccessory = (item: PayOverTimeItem): Maybe<string | TagProps> => {
  if (item.tag) {
    return {
      type: toTagType(item.tag.tagColor),
      text: item.tag.text,
      iconKey: null,
    };
  }

  return item.caption;
};

const toPressHandler = (
  action: Maybe<RouteNavigationAction>,
  navigateToRoute: HomeCardDependencies['navigateToRoute'],
): (() => void) | undefined => {
  if (!action) {
    return undefined;
  }

  return () => {
    navigateToRoute(action);
  };
};

const toCardNode = (
  item: PayOverTimeItem,
  navigateToRoute: HomeCardDependencies['navigateToRoute'],
): PayOverTimeCardNode => {
  const onCardClick = toPressHandler(item.onPress, navigateToRoute);

  return {
    id: item.id,
    name: VXLComponentId.HORIZONTAL_ACCOUNT_CARD,
    props: {
      accountIconProps: {
        product: AccountIconType.PayOverTime,
      },
      titleText: 'Pay Over Time',
      titleAccessory: mapTitleAccessory(item),
      amountText: item.balance?.formatted,
      disabled: false,
      onCardClick,
      analyticsId: 'payOverTimeAccountCardClicked',
      customAttributes: {
        account_type: 'pay_over_time',
        account_state: mapAccountStateToAnalytics(item.state),
      },
    },
  };
};

const payOverTimeTile = (
  tile: PayOverTimeItemsTile,
  { navigateToRoute }: HomeCardDependencies,
): Maybe<PayOverTimeTileNode> => {
  if (tile.status !== TileStatus.Loaded || tile.items.length === 0) {
    return null;
  }

  const components: PayOverTimeCardNode[] = [];

  for (const item of tile.items) {
    components.push(toCardNode(item, navigateToRoute));
  }

  return {
    id: tile.id,
    name: VXLGroupId.COMPONENT_GROUP,
    props: {
      components,
      gap: theme.spacing.s,
    },
  };
};

export default payOverTimeTile;
